/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../services';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModulesEnum, TwoFactorAuthApiEnum } from '../enums';
import { Observable } from 'rxjs';
import { UserSessionModel } from '../models';
import { catchError, timeout } from 'rxjs/operators';
import { throwError as observableThrowError } from 'rxjs/internal/observable/throwError';

@Injectable()
export class TwoFactorAuthApiService {

    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService,
        private http: HttpClient
    ) {
    }

    /**
     * Set up two-factor auth
     * @param id
     * @param phoneNumber
     * @param options
     */
    public setUp(id: string, phoneNumber?: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.post(TwoFactorAuthApiEnum.POST_SETUP(id), phoneNumber, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Verify two-factor auth.
     * @param id
     * @param otpCode
     * @param options
     */
    public verify(id: string, otpCode: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.post(TwoFactorAuthApiEnum.POST_VERIFY(id), otpCode, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Disable two-factor auth.
     * @param id
     * @param options
     */
    public disable(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.delete(TwoFactorAuthApiEnum.PUT_DELETE(id), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Not logged in API call to verify.
     * @param user
     * @param id
     * @param otpCode
     */
    public loginVerify(user: UserSessionModel, id: string, otpCode: any): Observable<any> {
        return new Observable((observer) => {
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');

            this.http.post(
                'https://' + user.applianceAddress + ':' +
                user.appliancePort + '/api/v1' +
                TwoFactorAuthApiEnum.POST_VERIFY(id),
                otpCode
            )
                .pipe(timeout(10000), catchError(err => observableThrowError(err)))
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }
}
